import { useState, useEffect, useCallback } from "react";
import SortableTableHead, { SortConfig, nextSort, sortData } from "./SortableTableHead";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollFadeWrapper } from "@/components/ui/scroll-fade-wrapper";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Search, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Clock, Stethoscope, ShieldAlert, RotateCcw, FileText, Download, Eye, Trash2, BellRing } from "lucide-react";
import { format, differenceInDays, parseISO } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface ProviderCredential {
  id: string;
  user_id: string;
  npi_number: string;
  medical_license_number: string;
  license_state: string;
  license_expiry: string;
  dea_number: string | null;
  dea_expiry: string | null;
  credential_status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  npi_verified?: boolean;
  baa_accepted_at?: string | null;
  baa_ip_address?: string | null;
  w9_status?: string;
  w9_file_url?: string | null;
  w9_collected_at?: string | null;
}

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: any }> = {
  pending_review: { label: "Pending", variant: "outline", icon: Clock },
  approved: { label: "Approved", variant: "default", icon: CheckCircle2 },
  rejected: { label: "Rejected", variant: "destructive", icon: XCircle },
  expired: { label: "Expired", variant: "destructive", icon: AlertTriangle },
  revoked: { label: "Revoked", variant: "destructive", icon: ShieldAlert },
};

/** Returns license health color: green (>90d), yellow (30–90d), red (<30d or expired) */
const getLicenseHealth = (expiryStr: string, status: string) => {
  if (!["approved", "pending_review"].includes(status)) return null;
  const days = differenceInDays(parseISO(expiryStr), new Date());
  if (days < 0) return { color: "text-red-600", bg: "bg-red-500", dot: "bg-red-500", label: "Expired", level: "red" as const };
  if (days <= 30) return { color: "text-red-600", bg: "bg-red-100", dot: "bg-red-500", label: `${days}d left`, level: "red" as const };
  if (days <= 90) return { color: "text-amber-600", bg: "bg-amber-100", dot: "bg-amber-500", label: `${days}d left`, level: "yellow" as const };
  return { color: "text-emerald-600", bg: "bg-emerald-100", dot: "bg-emerald-500", label: "Valid", level: "green" as const };
};

const ProviderCredentialing = () => {
  const [credentials, setCredentials] = useState<ProviderCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [subTab, setSubTab] = useState("pending");
  const [approveTarget, setApproveTarget] = useState<ProviderCredential | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ProviderCredential | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ProviderCredential | null>(null);
  const [reinstateTarget, setReinstateTarget] = useState<ProviderCredential | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [checkingLicenses, setCheckingLicenses] = useState(false);
  const { toast } = useToast();
  const [provSort, setProvSort] = useState<SortConfig>({ key: "created_at", direction: "desc" });

  const handleCleanupStrayCredentials = async () => {
    setCleaningUp(true);
    try {
      const { data, error } = await supabase.functions.invoke("cleanup-stray-credentials", { body: {} });
      if (error) throw error;
      const deleted = data?.deleted ?? 0;
      const credDeleted = data?.breakdown?.credentials?.deleted ?? 0;
      const credScanned = data?.breakdown?.credentials?.scanned ?? 0;
      const taxDeleted = data?.breakdown?.tax_info?.deleted ?? 0;
      const taxScanned = data?.breakdown?.tax_info?.scanned ?? 0;
      toast({
        title: deleted > 0
          ? `Cleaned up ${deleted} stray row${deleted === 1 ? "" : "s"}`
          : "Nothing to clean up",
        description:
          `Credentials: ${credDeleted} deleted / ${credScanned} scanned (>7d old). ` +
          `Tax info: ${taxDeleted} deleted / ${taxScanned} scanned.`,
      });
      if (deleted > 0) await fetchCredentials();
    } catch (e) {
      toast({
        title: "Cleanup failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setCleaningUp(false);
    }
  };

  const handleRunLicenseCheck = async () => {
    setCheckingLicenses(true);
    try {
      const { data, error } = await supabase.functions.invoke("check-license-expiry", { body: {} });
      if (error) throw error;
      const notified = data?.notified ?? 0;
      toast({
        title: notified > 0
          ? `Sent ${notified} expiry reminder${notified === 1 ? "" : "s"}`
          : "No reminders sent",
        description: notified > 0
          ? "Providers (and admins for ≤30d) were notified at the scheduled milestones."
          : "No approved licenses hit a 90/60/30/14/7/3/1-day milestone today.",
      });
    } catch (e) {
      toast({
        title: "License check failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setCheckingLicenses(false);
    }
  };

  const fetchCredentials = useCallback(async () => {
    setLoading(true);
    const { data: creds } = await supabase.from("provider_credentials" as any).select("*").order("created_at", { ascending: false });

    if (!creds || creds.length === 0) {
      setCredentials([]);
      setLoading(false);
      return;
    }

    const userIds = (creds as any[]).map((c: any) => c.user_id);
    const [{ data: profiles }, emailResult, { data: taxInfo }] = await Promise.all([
      supabase.from("profiles").select("user_id, first_name, last_name").in("user_id", userIds),
      supabase.functions.invoke("list-user-emails"),
      supabase.from("contractor_tax_info").select("user_id, w9_file_url, w9_collected_at").in("user_id", userIds),
    ]);

    const emailMap: Record<string, string> = emailResult.data?.emails || {};
    const profileMap = new Map((profiles || []).map((p: any) => [p.user_id, p]));
    const taxMap = new Map((taxInfo || []).map((t: any) => [t.user_id, t]));

    setCredentials(
      (creds as any[]).map((c: any) => {
        const profile = profileMap.get(c.user_id) as any;
        const tax = taxMap.get(c.user_id) as any;
        return {
          ...c,
          first_name: profile?.first_name || null,
          last_name: profile?.last_name || null,
          email: emailMap[c.user_id] || null,
          w9_file_url: tax?.w9_file_url || null,
          w9_collected_at: tax?.w9_collected_at || null,
        };
      })
    );
    setLoading(false);
  }, []);

  useEffect(() => { fetchCredentials(); }, [fetchCredentials]);

  const sendProviderStatusEmail = async (provider: ProviderCredential, status: "approved" | "rejected" | "revoked", reason?: string) => {
    if (!provider.email) return;
    try {
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "provider-application-status",
          recipientEmail: provider.email,
          idempotencyKey: `provider-status-${provider.user_id}-${status}-${Date.now()}`,
          templateData: {
            providerName: provider.last_name || provider.first_name || undefined,
            status,
            rejectionReason: reason || undefined,
          },
        },
      });
    } catch (e) {
      console.error("Failed to send provider status email", e);
    }
  };

  const logProviderAudit = async (action: string, provider: ProviderCredential, metadata?: Record<string, unknown>) => {
    try {
      await supabase.functions.invoke("log-audit", {
        body: {
          action,
          resource_type: "provider_credential",
          resource_id: provider.id,
          metadata: { target_user_id: provider.user_id, provider_name: `${provider.first_name || ""} ${provider.last_name || ""}`.trim(), ...metadata },
        },
      });
    } catch {
      console.warn("Audit log failed silently");
    }
  };

  const handleApprove = async () => {
    if (!approveTarget) return;
    setProcessing(true);
    const { error } = await (supabase.rpc as any)("approve_provider", { target_user_id: approveTarget.user_id });
    if (error) {
      toast({ title: "Approval failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Provider approved", description: `${approveTarget.first_name || ""} ${approveTarget.last_name || ""} now has provider access.` });
      const providerName = [approveTarget.first_name, approveTarget.last_name].filter(Boolean).join(" ") || undefined;
      await Promise.all([
        sendProviderStatusEmail(approveTarget, "approved"),
        logProviderAudit("approve_provider", approveTarget),
        // Send welcome email
        approveTarget.email
          ? supabase.functions.invoke("send-transactional-email", {
              body: {
                templateName: "role-approved-welcome",
                recipientEmail: approveTarget.email,
                idempotencyKey: `role-approved-provider-${approveTarget.user_id}`,
                templateData: { recipientName: providerName, approvedRole: "provider" },
              },
            })
          : Promise.resolve(),
        // Update role_request status if one exists
        supabase
          .from("role_requests")
          .update({ status: "approved", reviewed_by: approveTarget.user_id, reviewed_at: new Date().toISOString() })
          .eq("user_id", approveTarget.user_id)
          .eq("requested_role", "provider")
          .eq("status", "pending"),
      ]);
    }
    setApproveTarget(null);
    setProcessing(false);
    fetchCredentials();
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setProcessing(true);
    const reason = rejectionReason || "Rejected by admin";
    await supabase.from("provider_credentials" as any).update({ rejection_reason: reason }).eq("user_id", rejectTarget.user_id);
    const { error } = await (supabase.rpc as any)("revoke_provider", { target_user_id: rejectTarget.user_id, reason: "rejected" });
    if (error) {
      toast({ title: "Rejection failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Provider rejected" });
      await Promise.all([
        sendProviderStatusEmail(rejectTarget, "rejected", reason),
        logProviderAudit("reject_provider", rejectTarget, { reason }),
      ]);
    }
    setRejectTarget(null);
    setRejectionReason("");
    setProcessing(false);
    fetchCredentials();
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setProcessing(true);
    const { error } = await (supabase.rpc as any)("revoke_provider", { target_user_id: revokeTarget.user_id, reason: "expired" });
    if (error) {
      toast({ title: "Revocation failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Provider access revoked" });
      await Promise.all([
        sendProviderStatusEmail(revokeTarget, "revoked"),
        logProviderAudit("revoke_provider", revokeTarget, { previous_status: revokeTarget.credential_status }),
      ]);
    }
    setRevokeTarget(null);
    setProcessing(false);
    fetchCredentials();
  };

  const handleReinstate = async () => {
    if (!reinstateTarget) return;
    setProcessing(true);
    const { error } = await (supabase.rpc as any)("approve_provider", { target_user_id: reinstateTarget.user_id });
    if (error) {
      toast({ title: "Reinstatement failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Provider reinstated", description: `${reinstateTarget.first_name || ""} ${reinstateTarget.last_name || ""} has been restored to active status.` });
      await Promise.all([
        sendProviderStatusEmail(reinstateTarget, "approved"),
        logProviderAudit("reinstate_provider", reinstateTarget, { previous_status: reinstateTarget.credential_status }),
      ]);
    }
    setReinstateTarget(null);
    setProcessing(false);
    fetchCredentials();
  };

  const provAccessor = (item: ProviderCredential, key: string): any => {
    if (key === "name") return `${item.first_name || ""} ${item.last_name || ""}`.trim();
    return (item as any)[key];
  };

  const filtered = sortData(credentials.filter((c) => {
    const name = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
    const term = search.toLowerCase();
    const matchesSearch = !term || name.includes(term) || c.npi_number.includes(term) || (c.email || "").toLowerCase().includes(term);

    if (subTab === "pending") return matchesSearch && c.credential_status === "pending_review";
    if (subTab === "active") return matchesSearch && c.credential_status === "approved";
    if (subTab === "rejected") return matchesSearch && c.credential_status === "rejected";
    if (subTab === "revoked") return matchesSearch && ["expired", "revoked"].includes(c.credential_status);
    return matchesSearch;
  }), provSort, provAccessor);

  const getExpiryBadge = (dateStr: string) => {
    const days = differenceInDays(parseISO(dateStr), new Date());
    if (days < 0) return <Badge variant="destructive" className="text-xs">Expired</Badge>;
    if (days <= 30) return <Badge className="text-xs bg-amber-500/20 text-amber-700 border-amber-500/30">Expires in {days}d</Badge>;
    return null;
  };

  const pendingCount = credentials.filter((c) => c.credential_status === "pending_review").length;
  const revokedCount = credentials.filter((c) => ["expired", "revoked"].includes(c.credential_status)).length;
  const rejectedCount = credentials.filter((c) => c.credential_status === "rejected").length;
  const expiringCount = credentials.filter((c) => c.credential_status === "approved" && differenceInDays(parseISO(c.license_expiry), new Date()) <= 30).length;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><Stethoscope size={14} /> Total Providers</div>
            <p className="text-2xl font-bold mt-1">{credentials.length}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-amber-600 text-sm"><Clock size={14} /> Pending Review</div>
            <p className="text-2xl font-bold mt-1">{pendingCount}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-primary text-sm"><CheckCircle2 size={14} /> Active</div>
            <p className="text-2xl font-bold mt-1">{credentials.filter((c) => c.credential_status === "approved").length}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive text-sm"><AlertTriangle size={14} /> Expiring Soon</div>
            <p className="text-2xl font-bold mt-1">{expiringCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* License Health Legend */}
      <div className="flex items-center gap-6 text-xs text-muted-foreground px-1">
        <span className="font-medium text-foreground">License Status:</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Green — Valid (&gt;90 days)</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" /> Yellow — Expiring (30–90 days)</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> Red — Critical (&lt;30 days) — Cannot be assigned patients</span>
      </div>

      <Card className="border-border">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="font-display text-xl">Provider Credentials</CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search providers..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-64" />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 h-9"
              onClick={handleCleanupStrayCredentials}
              disabled={cleaningUp}
              title="Delete pending_review credentials >7d old with no active role request"
            >
              <Trash2 size={14} />
              {cleaningUp ? "Cleaning…" : "Cleanup stray"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 h-9"
              onClick={handleRunLicenseCheck}
              disabled={checkingLicenses}
              title="Run check-license-expiry now and send any due 90/60/30/14/7/3/1-day reminders"
            >
              <BellRing size={14} />
              {checkingLicenses ? "Checking…" : "Run license check"}
            </Button>
            <Button variant="outline" size="icon" onClick={fetchCredentials}><RefreshCw size={14} /></Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={subTab} onValueChange={setSubTab} className="space-y-4">
            <ScrollFadeWrapper fadeColor="from-secondary">
            <TabsList className="flex overflow-x-auto no-scrollbar h-auto gap-1 p-1 bg-secondary w-full justify-start">
              <TabsTrigger value="pending" className="gap-1.5 shrink-0 min-h-11">
                <Clock size={14} /> Pending {pendingCount > 0 && <Badge variant="destructive" className="text-xs ml-1 h-5 px-1.5">{pendingCount}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="active" className="gap-1.5 shrink-0 min-h-11"><CheckCircle2 size={14} /> Active</TabsTrigger>
              <TabsTrigger value="revoked" className="gap-1.5 shrink-0 min-h-11">
                <ShieldAlert size={14} /> Revoked {revokedCount > 0 && <Badge variant="outline" className="text-xs ml-1 h-5 px-1.5">{revokedCount}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="rejected" className="gap-1.5 shrink-0 min-h-11">
                <XCircle size={14} /> Rejected {rejectedCount > 0 && <Badge variant="outline" className="text-xs ml-1 h-5 px-1.5">{rejectedCount}</Badge>}
              </TabsTrigger>
            </TabsList>
            </ScrollFadeWrapper>

            <TabsContent value={subTab}>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filtered.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">No providers in this category.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead label="Name" sortKey="name" currentSort={provSort} onSort={(k) => setProvSort(nextSort(provSort, k))} />
                      <SortableTableHead label="NPI" sortKey="npi_number" currentSort={provSort} onSort={(k) => setProvSort(nextSort(provSort, k))} />
                      <SortableTableHead label="License" sortKey="medical_license_number" currentSort={provSort} onSort={(k) => setProvSort(nextSort(provSort, k))} />
                      <SortableTableHead label="State" sortKey="license_state" currentSort={provSort} onSort={(k) => setProvSort(nextSort(provSort, k))} />
                      <SortableTableHead label="License Expiry" sortKey="license_expiry" currentSort={provSort} onSort={(k) => setProvSort(nextSort(provSort, k))} />
                      {(subTab === "revoked" || subTab === "rejected") && <TableHead>Reason</TableHead>}
                      {(subTab === "revoked" || subTab === "rejected") && <TableHead>Reviewed</TableHead>}
                      {subTab !== "revoked" && subTab !== "rejected" && <TableHead>DEA</TableHead>}
                      <TableHead className="text-center">NPI</TableHead>
                      <TableHead className="text-center">BAA</TableHead>
                      <TableHead className="text-center">W-9</TableHead>
                      <SortableTableHead label="Status" sortKey="credential_status" currentSort={provSort} onSort={(k) => setProvSort(nextSort(provSort, k))} />
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                     {filtered.map((c) => {
                      const cfg = statusConfig[c.credential_status] || statusConfig.pending_review;
                      const Icon = cfg.icon;
                      const health = getLicenseHealth(c.license_expiry, c.credential_status);
                      return (
                        <TableRow key={c.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {health && (
                                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${health.dot}`} title={`License: ${health.label}`} />
                              )}
                              <div>
                                <span className={health ? health.color : ""}>
                                  {c.first_name || c.last_name ? `${c.first_name || ""} ${c.last_name || ""}`.trim() : "—"}
                                </span>
                                {health && health.level !== "green" && (
                                  <span className={`ml-2 text-[10px] font-semibold uppercase ${health.color}`}>
                                    {health.label}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-xs text-muted-foreground ml-[18px]">{c.email || ""}</div>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{c.npi_number}</TableCell>
                          <TableCell className="text-sm">{c.medical_license_number}</TableCell>
                          <TableCell>{c.license_state}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{format(parseISO(c.license_expiry), "MMM d, yyyy")}</span>
                              {getExpiryBadge(c.license_expiry)}
                            </div>
                          </TableCell>
                          {(subTab === "revoked" || subTab === "rejected") ? (
                            <>
                              <TableCell>
                                <p className="text-sm text-muted-foreground max-w-[200px]" title={c.rejection_reason || c.credential_status}>
                                  {c.rejection_reason || (c.credential_status === "expired" ? "License expired" : c.credential_status === "revoked" ? "Revoked by admin" : "—")}
                                </p>
                              </TableCell>
                              <TableCell>
                                {c.reviewed_at ? (
                                  <span className="text-xs text-muted-foreground">{format(parseISO(c.reviewed_at), "MMM d, yyyy")}</span>
                                ) : (
                                  <span className="text-muted-foreground text-sm">—</span>
                                )}
                              </TableCell>
                            </>
                          ) : (
                            <TableCell>
                              {c.dea_number ? (
                                <div>
                                  <span className="text-sm">{c.dea_number}</span>
                                  {c.dea_expiry && (
                                    <div className="flex items-center gap-1 mt-0.5">
                                      <span className="text-xs text-muted-foreground">{format(parseISO(c.dea_expiry), "MMM yyyy")}</span>
                                      {getExpiryBadge(c.dea_expiry)}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">—</span>
                              )}
                            </TableCell>
                          )}
                          {/* Onboarding status icons */}
                          <TableCell className="text-center">
                            {c.npi_verified ? (
                              <CheckCircle2 size={16} className="inline text-primary" />
                            ) : (
                              <XCircle size={16} className="inline text-muted-foreground/40" />
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {c.baa_accepted_at ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button className="inline-flex items-center gap-1 hover:opacity-70 transition-opacity" title="View BAA details">
                                    <CheckCircle2 size={16} className="text-primary" />
                                    <Eye size={12} className="text-muted-foreground" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-80 text-sm space-y-3 max-h-[400px] overflow-y-auto">
                                  <div className="font-semibold flex items-center gap-2"><FileText size={14} /> Business Associate Agreement</div>
                                  <div className="space-y-1 text-muted-foreground">
                                    <p><span className="font-medium text-foreground">Signed:</span> {format(parseISO(c.baa_accepted_at), "MMM d, yyyy 'at' h:mm a")}</p>
                                    <p><span className="font-medium text-foreground">IP Address:</span> {c.baa_ip_address || "Not recorded"}</p>
                                    <p><span className="font-medium text-foreground">Signer:</span> {c.first_name || ""} {c.last_name || ""}</p>
                                  </div>
                                  <div className="border-t pt-2">
                                    <p className="text-xs font-medium text-foreground mb-1">Agreement Summary</p>
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                      The provider electronically accepted the HIPAA Business Associate Agreement, agreeing to safeguard Protected Health Information (PHI), comply with the Security Rule, report breaches, and maintain compliance with all applicable HIPAA regulations. This agreement remains in effect for the duration of their provider relationship with Elara Health.
                                    </p>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <XCircle size={16} className="inline text-muted-foreground/40" />
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {c.w9_status === "uploaded" || c.w9_status === "verified" ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button className="inline-flex items-center gap-1 hover:opacity-70 transition-opacity" title="View W-9 details">
                                    <CheckCircle2 size={16} className="text-primary" />
                                    <Eye size={12} className="text-muted-foreground" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 text-sm space-y-3">
                                  <div className="font-semibold flex items-center gap-2"><FileText size={14} /> W-9 Tax Form</div>
                                  <div className="space-y-1 text-muted-foreground">
                                    <p><span className="font-medium text-foreground">Status:</span> {c.w9_status === "verified" ? "Verified" : "Uploaded"}</p>
                                    {c.w9_collected_at && (
                                      <p><span className="font-medium text-foreground">Submitted:</span> {format(parseISO(c.w9_collected_at), "MMM d, yyyy 'at' h:mm a")}</p>
                                    )}
                                  </div>
                                  {c.w9_file_url && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="w-full gap-2 text-xs"
                                      onClick={async () => {
                                        try {
                                          const { data, error } = await supabase.storage.from("w9-uploads").download(c.w9_file_url!);
                                          if (error || !data) throw error || new Error("Download failed");
                                          const url = URL.createObjectURL(data);
                                          const a = document.createElement("a");
                                          a.href = url;
                                          a.download = `w9-${(c.first_name || "").toLowerCase()}-${(c.last_name || "").toLowerCase()}.pdf`;
                                          document.body.appendChild(a);
                                          a.click();
                                          document.body.removeChild(a);
                                          URL.revokeObjectURL(url);
                                        } catch (err: any) {
                                          toast({ title: "Download failed", description: err.message, variant: "destructive" });
                                        }
                                      }}
                                    >
                                      <Download size={12} /> Download W-9
                                    </Button>
                                  )}
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <XCircle size={16} className="inline text-muted-foreground/40" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={cfg.variant} className="gap-1 text-xs">
                              <Icon size={12} /> {cfg.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1.5">
                              {c.credential_status === "pending_review" && (() => {
                                const onboardingComplete = c.npi_verified && !!c.baa_accepted_at && (c.w9_status === "uploaded" || c.w9_status === "verified");
                                return (
                                  <>
                                    <Button size="sm" variant="default" className="gap-1 h-7 text-xs" onClick={() => setApproveTarget(c)} disabled={!onboardingComplete} title={!onboardingComplete ? "Provider must complete NPI verification, BAA, and W-9 first" : undefined}>
                                      <CheckCircle2 size={12} /> Approve
                                    </Button>
                                    <Button size="sm" variant="destructive" className="gap-1 h-7 text-xs" onClick={() => setRejectTarget(c)}>
                                      <XCircle size={12} /> Reject
                                    </Button>
                                  </>
                                );
                              })()}
                              {c.credential_status === "approved" && (
                                <Button size="sm" variant="outline" className="gap-1 h-7 text-xs text-destructive" onClick={() => setRevokeTarget(c)}>
                                  <ShieldAlert size={12} /> Revoke
                                </Button>
                              )}
                              {["rejected", "expired", "revoked"].includes(c.credential_status) && (
                                <Button size="sm" variant="outline" className="gap-1 h-7 text-xs text-primary" onClick={() => setReinstateTarget(c)}>
                                  <RotateCcw size={12} /> Reinstate
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <AlertDialog open={!!approveTarget} onOpenChange={(o) => !o && setApproveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Provider</AlertDialogTitle>
            <AlertDialogDescription>
              Grant provider access to <strong>{approveTarget?.first_name} {approveTarget?.last_name}</strong> (NPI: {approveTarget?.npi_number})?
              They will be able to view assessments, prescribe, and manage patients.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleApprove} disabled={processing}>
              {processing ? "Approving..." : "Approve"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Dialog */}
      <AlertDialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectionReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Provider Application</AlertDialogTitle>
            <AlertDialogDescription>
              Reject the application from <strong>{rejectTarget?.first_name} {rejectTarget?.last_name}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6 pb-2">
            <Textarea placeholder="Reason for rejection (optional)" value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} rows={3} />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReject} disabled={processing} className="bg-destructive hover:bg-destructive/90">
              {processing ? "Rejecting..." : "Reject"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Dialog */}
      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Provider Access</AlertDialogTitle>
            <AlertDialogDescription>
              Remove provider access from <strong>{revokeTarget?.first_name} {revokeTarget?.last_name}</strong>? Their credentials will be preserved and can be reinstated later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevoke} disabled={processing} className="bg-destructive hover:bg-destructive/90">
              {processing ? "Revoking..." : "Revoke Access"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reinstate Dialog */}
      <AlertDialog open={!!reinstateTarget} onOpenChange={(o) => !o && setReinstateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reinstate Provider</AlertDialogTitle>
            <AlertDialogDescription>
              Restore provider access for <strong>{reinstateTarget?.first_name} {reinstateTarget?.last_name}</strong> (NPI: {reinstateTarget?.npi_number})?
              They will regain full provider dashboard access and the ability to manage patients.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReinstate} disabled={processing}>
              {processing ? "Reinstating..." : "Reinstate Provider"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProviderCredentialing;
